const faqPreviewAnswerTextSelector = '.product__faq-preview-answer-text';
const faqPreviewTransitionProperty = 'max-height';

function getFaqPreviewCollapsedHeight(answerText) {
  const firstTextBlock = answerText.querySelector('p') || answerText;
  const computedStyle = window.getComputedStyle(firstTextBlock);
  const fontSize = parseFloat(computedStyle.fontSize);
  let lineHeight = parseFloat(computedStyle.lineHeight);

  if (Number.isNaN(lineHeight)) {
    lineHeight = fontSize * 1.5;
  }

  return Math.ceil(lineHeight * 2) + 1;
}

function setFaqPreviewCollapsedHeight(answerText) {
  answerText.style.setProperty('--product-faq-preview-collapsed-height', `${getFaqPreviewCollapsedHeight(answerText)}px`);
}

function getFaqPreviewFullHeight(answerText) {
  const previousMaxHeight = answerText.style.maxHeight;

  answerText.style.maxHeight = 'none';
  const fullHeight = answerText.scrollHeight;
  answerText.style.maxHeight = previousMaxHeight;

  return fullHeight;
}

function isFaqPreviewTextOverflowing(answerText) {
  setFaqPreviewCollapsedHeight(answerText);
  return getFaqPreviewFullHeight(answerText) > getFaqPreviewCollapsedHeight(answerText);
}

function updateFaqPreviewOverflowState(answerText) {
  const answer = answerText.closest('[data-product-faq-preview-answer]');

  if (!answer || answer.classList.contains('is-expanded')) return;

  answer.classList.toggle('is-overflowing', isFaqPreviewTextOverflowing(answerText));
}

function isFaqPreviewAnimating(answer) {
  return answer.dataset.faqPreviewAnimating === 'true';
}

function setFaqPreviewAnimating(answer, isAnimating) {
  if (isAnimating) {
    answer.dataset.faqPreviewAnimating = 'true';
  } else {
    delete answer.dataset.faqPreviewAnimating;
  }
}

function getFaqPreviewTransitionDurationMs(answerText) {
  const duration = parseFloat(window.getComputedStyle(answerText).transitionDuration) || 0;
  const delay = parseFloat(window.getComputedStyle(answerText).transitionDelay) || 0;

  return (duration + delay) * 1000 + 50;
}

function waitForFaqPreviewTransition(answerText, onComplete) {
  let completed = false;

  const finish = () => {
    if (completed) return;

    completed = true;
    answerText.removeEventListener('transitionend', onTransitionEnd);
    window.clearTimeout(fallbackTimeout);
    onComplete();
  };

  const onTransitionEnd = (event) => {
    if (event.target !== answerText || event.propertyName !== faqPreviewTransitionProperty) return;

    finish();
  };

  const fallbackTimeout = window.setTimeout(finish, getFaqPreviewTransitionDurationMs(answerText));

  answerText.addEventListener('transitionend', onTransitionEnd);
}

function setExpandedFaqPreviewHeight(answerText) {
  answerText.style.maxHeight = `${getFaqPreviewFullHeight(answerText)}px`;
}

function expandFaqPreview(answer, answerText, toggle) {
  if (!answer.classList.contains('is-overflowing')) return;

  setFaqPreviewCollapsedHeight(answerText);

  const collapsedHeight = getFaqPreviewCollapsedHeight(answerText);
  const fullHeight = getFaqPreviewFullHeight(answerText);

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    answer.classList.add('is-expanded');
    toggle.setAttribute('aria-expanded', 'true');
    answerText.style.maxHeight = 'none';
    return;
  }

  setFaqPreviewAnimating(answer, true);
  answerText.style.maxHeight = `${collapsedHeight}px`;
  void answerText.offsetHeight;
  answer.classList.add('is-expanded');
  toggle.setAttribute('aria-expanded', 'true');

  requestAnimationFrame(() => {
    answerText.style.maxHeight = `${fullHeight}px`;

    waitForFaqPreviewTransition(answerText, () => {
      answerText.style.maxHeight = 'none';
      setFaqPreviewAnimating(answer, false);
    });
  });
}

function collapseFaqPreview(answer, answerText, toggle) {
  setFaqPreviewCollapsedHeight(answerText);

  const collapsedHeight = getFaqPreviewCollapsedHeight(answerText);
  const fullHeight = getFaqPreviewFullHeight(answerText);

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    answer.classList.remove('is-expanded');
    toggle.setAttribute('aria-expanded', 'false');
    answerText.style.maxHeight = '';
    updateFaqPreviewOverflowState(answerText);
    return;
  }

  setFaqPreviewAnimating(answer, true);
  answerText.style.maxHeight = `${fullHeight}px`;
  void answerText.offsetHeight;
  answer.classList.remove('is-expanded');
  toggle.setAttribute('aria-expanded', 'false');

  requestAnimationFrame(() => {
    answerText.style.maxHeight = `${collapsedHeight}px`;

    waitForFaqPreviewTransition(answerText, () => {
      answerText.style.maxHeight = '';
      setFaqPreviewAnimating(answer, false);
      updateFaqPreviewOverflowState(answerText);
    });
  });
}

function initializeFaqPreviewAnswers() {
  document.querySelectorAll(faqPreviewAnswerTextSelector).forEach((answerText) => {
    const answer = answerText.closest('[data-product-faq-preview-answer]');

    if (answer && isFaqPreviewAnimating(answer)) return;

    setFaqPreviewCollapsedHeight(answerText);
    updateFaqPreviewOverflowState(answerText);

    if (answer?.classList.contains('is-expanded')) {
      answerText.style.maxHeight = 'none';
    } else {
      answerText.style.maxHeight = '';
    }
  });
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('.product__faq-preview-toggle');

  if (!toggle) return;

  const answer = toggle.closest('[data-product-faq-preview-answer]');
  const answerText = answer ? answer.querySelector(faqPreviewAnswerTextSelector) : null;

  if (!answer || !answerText || isFaqPreviewAnimating(answer)) return;

  if (!answer.classList.contains('is-expanded')) {
    expandFaqPreview(answer, answerText, toggle);
    return;
  }

  collapseFaqPreview(answer, answerText, toggle);
});

window.addEventListener('resize', () => {
  initializeFaqPreviewAnswers();
});

document.addEventListener('DOMContentLoaded', initializeFaqPreviewAnswers);
window.addEventListener('load', initializeFaqPreviewAnswers);
initializeFaqPreviewAnswers();
